import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import parse from 'csv-parse/lib/browser/sync';
import { compact } from 'lodash-es';
import { sortAlphabetically } from '~/infra/utils';
import pluralize from '~/utils/pluralize';
export const isBoolField = (field)=>[
        'yes',
        'no'
    ].includes(field.toLowerCase());
export const isBoolTrue = (bool)=>bool.toLowerCase() === 'yes';
export const getEmptyStringError = (fieldName)=>"".concat(fieldName, " must have a value");
export const invalidEmailError = 'Please provide a valid email address';
export const phoneNumberError = 'This value must match the international telephone number format';
// e164 is a standard for "International Telephone Input Validation". This regex is the same one the BE uses.
export const e164Regex = /^\+[1-9]?[0-9]{7,14}$/;
export const normalizeSpaces = (value)=>value.replace(/[\s\r\n]+/g, ' ').trim();
/** Makes sure that a phone number starts with a "+" as the initial character. Doesn't touch an empty phone number. */ export const normalizePhoneNumber = (phone)=>phone ? "".concat(phone.startsWith('+') ? '' : '+').concat(phone) : phone;
export const createErrorObject = (headerName, rowNumber, error)=>// eslint-disable-next-line no-console
    console.error('Column "'.concat(headerName, '": ').concat(error, ".")) || {
        rowNumber,
        message: 'Column "'.concat(headerName, '": ').concat(error, ".")
    };
export const createRowErrorsObject = (errorObjects)=>{
    if (!errorObjects.length) return null;
    const sortedMessages = sortAlphabetically(errorObjects, 'message', {
        ascending: true,
        ignoreCase: true
    });
    return sortedMessages.reduce((rowToMessages, error)=>{
        const { rowNumber, message } = error;
        const key = (rowNumber === null || rowNumber === void 0 ? void 0 : rowNumber.toString()) || '0';
        rowToMessages[key] = rowToMessages[key] ? rowToMessages[key].concat(message) : [
            message
        ];
        return rowToMessages;
    }, {});
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseCsv = function parseCsv(data, csvColConfig) {
    let ignoredColumns = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : [];
    const ignoredColumnsLowerCase = new Set(ignoredColumns.map((c)=>c.trim().toLowerCase()));
    return parse(data, {
        skip_empty_lines: true,
        trim: true,
        columns: (header)=>{
            const trimAndLowerCase = (str)=>str.trim().toLowerCase();
            const lowerCaseHeader = header.map(trimAndLowerCase);
            for (const columnName of Object.keys(csvColConfig)){
                if (!lowerCaseHeader.includes(trimAndLowerCase(columnName))) {
                    throw new Error('The column "'.concat(columnName, '" is missing'));
                }
            }
            return header.map((columnName)=>{
                const lowerCaseColumnName = trimAndLowerCase(columnName);
                if (ignoredColumnsLowerCase.has(lowerCaseColumnName)) {
                    return undefined;
                }
                if (csvColConfig[lowerCaseColumnName]) {
                    return csvColConfig[lowerCaseColumnName].inputName;
                } else {
                    throw new Error('Could not parse unsupported column header: "'.concat(columnName, '"'));
                }
            });
        },
        cast: (value, context)=>{
            if (context.header) return value;
            const columnConfig = Object.values(csvColConfig).find((config)=>config.inputName === context.column);
            if (columnConfig === null || columnConfig === void 0 ? void 0 : columnConfig.manipulateValue) {
                return columnConfig.manipulateValue(value);
            }
            return value;
        }
    });
};
export function validateCsvColumns(data, csvColConfig) {
    const expectedColumns = new Set(Object.values(csvColConfig).map((c)=>c.inputName));
    const receivedColumns = new Set(Object.keys(data[0]));
    const unexpectedColumns = [];
    receivedColumns.forEach((column)=>{
        if (!expectedColumns.has(column)) {
            unexpectedColumns.push(column);
        } else {
            expectedColumns.delete(column);
        }
    });
    function columnNameFromInputName(inputName) {
        const columnEntry = Object.entries(csvColConfig).find((param)=>{
            let [, value] = param;
            return value.inputName === inputName;
        });
        return columnEntry ? columnEntry[0] : inputName;
    }
    if (expectedColumns.size !== 0) throw new Error("".concat(pluralize('Column', expectedColumns.size), " ").concat(expectedColumns.size > 1 ? 'are' : 'is', " missing: ").concat(Array.from(expectedColumns).map(columnNameFromInputName).join(', ')));
    if (unexpectedColumns.length > 0) throw new Error("Unexpected columns: ".concat(unexpectedColumns.join(', ')));
}
export function addCsvLineNumbers(data) {
    return data.map((item, i)=>_object_spread_props(_object_spread({}, item), {
            lineNumber: i + 2
        }));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function validateCsvRow(rowObject, csvColConfig, extraValidation, now) {
    return compact(Object.keys(csvColConfig).map((columnName)=>{
        const columnConfig = csvColConfig[columnName];
        const { inputName, required, requiredError, validate, validateError, requiredWhen, getForbiddenReason } = columnConfig;
        const value = rowObject[inputName];
        const forbiddenReason = getForbiddenReason === null || getForbiddenReason === void 0 ? void 0 : getForbiddenReason(rowObject);
        if (forbiddenReason) {
            if (!value) {
                return;
            }
            return createErrorObject(columnName, rowObject.lineNumber, forbiddenReason);
        }
        if (extraValidation) {
            const extraError = extraValidation(columnConfig, columnName);
            if (extraError) return extraError;
        }
        const isRequired = required || (requiredWhen === null || requiredWhen === void 0 ? void 0 : requiredWhen(rowObject));
        if (isRequired && !value && requiredError) {
            return requiredError(columnName, rowObject.lineNumber);
        }
        if (validate && validateError) {
            const validationResult = validate(value, {
                rowObject,
                now
            });
            const isBoolean = typeof validationResult === 'boolean';
            const isValid = isBoolean ? validationResult : validationResult === null || validationResult === void 0 ? void 0 : validationResult.valid;
            const errorType = isBoolean ? undefined : validationResult.errorType;
            return !isValid && validateError(columnName, rowObject.lineNumber, errorType);
        }
    }));
}
